import * as React from 'react';
import {
  Box, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, List, ListItemButton, ListItemIcon, ListItemText, Checkbox,
  InputAdornment, Typography, Stack, Paper, Divider,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectDialogFieldProps {
  label: string;
  options: MultiSelectOption[];
  values: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Multi-sélection 100 % MUI dans le style de CodeLookupField :
 * - TextField read-only affichant les codes sélectionnés
 * - Dialog avec recherche locale + cases à cocher + « Tout cocher »
 */
export default function MultiSelectDialogField({
  label, options, values, onChange,
  disabled = false, placeholder,
}: MultiSelectDialogFieldProps) {
  const [open, setOpen] = React.useState(false);
  const [pending, setPending] = React.useState<string[]>([]);
  const [searchText, setSearchText] = React.useState('');

  const filtered = options.filter((o) =>
    !searchText.trim() ||
    o.value.toLowerCase().includes(searchText.toLowerCase()) ||
    o.label.toLowerCase().includes(searchText.toLowerCase())
  );

  const allChecked = options.length > 0 && pending.length === options.length;
  const displayValue = allChecked ? 'Toutes' : values.join(', ');

  const toggle = (v: string) => {
    setPending((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    );
  };

  const toggleAll = () => {
    setPending(allChecked ? [] : options.map((o) => o.value));
  };

  const handleOpen = () => {
    if (disabled) return;
    setPending(values);
    setSearchText('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchText('');
  };

  const handleValidate = () => {
    onChange(pending);
    handleClose();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  return (
    <>
      <TextField
        label={label}
        size="small"
        fullWidth
        disabled={disabled}
        value={displayValue}
        placeholder={placeholder || 'Cliquez pour sélectionner...'}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          sx: { cursor: 'pointer' },
          endAdornment: (
            <InputAdornment position="end">
              {values.length > 0 && !disabled && (
                <IconButton size="small" edge="end" onClick={handleClear} title="Effacer">
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" edge="end" onClick={handleOpen} disabled={disabled}>
                <ArrowDropDownIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        helperText={
          values.length > 0 && !allChecked
            ? `${values.length} sélectionnée(s)`
            : undefined
        }
        FormHelperTextProps={{ sx: { mx: 0.5, mt: 0, lineHeight: 1.2, fontSize: 10.5, color: '#64748b' } }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary">{label}</Typography>
          <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Rechercher..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />

          <Paper variant="outlined" sx={{ borderRadius: '10px', overflow: 'hidden' }}>
            <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
              <List dense disablePadding>
                <ListItemButton onClick={toggleAll} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={allChecked}
                      indeterminate={pending.length > 0 && !allChecked}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={allChecked ? 'Tout décocher' : 'Tout cocher'}
                    primaryTypographyProps={{ fontWeight: 700, fontSize: 13 }}
                  />
                </ListItemButton>
                <Divider />
                {filtered.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4, color: '#94a3b8', fontSize: 13 }}>
                    Aucun résultat.
                  </Box>
                ) : (
                  filtered.map((opt) => (
                    <ListItemButton key={opt.value} onClick={() => toggle(opt.value)} sx={{ py: 0.25 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          size="small"
                          checked={pending.includes(opt.value)}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: 13 }} />
                    </ListItemButton>
                  ))
                )}
              </List>
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Annuler</Button>
          <Button variant="contained" onClick={handleValidate} sx={{ borderRadius: '10px', textTransform: 'none' }}>
            Valider ({pending.length})
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
